import { put, call, getContext, select, cancel } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { getBetPosition, TGetBetPositionResult } from '../../../../services/auction/getBetPosition';
import {
  loadBetPositionFail,
  loadBetPositionStart,
  loadBetPositionSuccess,
  resetBetPositionCache,
} from '../../../reducers/betPosition';
import { getBetPositionParams, validateBetPositionParams } from './utils';
import { IAppState, TActions } from '../../../types';
import { SELECT_PUBLISH_TERMS_SERVICE } from '../../../reducers';
import { selectBetPosition } from '../../../../selectors/betPosition';
import { getIsNewPublishTermsEnabled } from '../../../../utils/publishTerms';
import { IConfig } from '@cian/config/shared';
import { selectIsAuctionBetEnabled, selectStepBet } from '../../../../selectors/auction';
import { selectActiveServiceId } from '../../../../selectors/publishTerms';
import { selectIsAddJKState } from '../../../../selectors/jk';

/**
 * Экшены, для которых требуется кэшировать запрос.
 * Если значение для текущего экшена найдено в кэше - запрос не выполняется.
 */
const CACHED_ACTIONS = [SELECT_PUBLISH_TERMS_SERVICE];

export function* loadBetPosition(action: TActions) {
  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const config: IConfig = yield getContext('config');

  const state: IAppState = yield select();

  const stepBet: ReturnType<typeof selectStepBet> = yield select(selectStepBet);
  const betPosition: ReturnType<typeof selectBetPosition> = yield select(selectBetPosition);
  const selectedServiceId: ReturnType<typeof selectActiveServiceId> = yield select(selectActiveServiceId);
  const isAuctionBetEnabled: ReturnType<typeof selectIsAuctionBetEnabled> = yield select(selectIsAuctionBetEnabled);

  const { abExperiments, auction, offer } = state;

  const isAddJKState = selectIsAddJKState(state);

  const { search } = window.location;

  const isFeatureEnabled = getIsNewPublishTermsEnabled({ search, config, abExperiments, isAddJKState });
  const isCacheEnabled = CACHED_ACTIONS.includes(action.type);

  // данные для текущей услуги уже загружены
  const alreadyLoaded = isCacheEnabled && !!betPosition;

  if (!isFeatureEnabled || !selectedServiceId || alreadyLoaded) {
    yield cancel();
  }

  // Выше стоит проверка на selectedServiceId, но ТС не выводит
  const serviceId = selectedServiceId as string;

  yield put(loadBetPositionStart());

  const parameters = getBetPositionParams({ auction, offer, serviceId });
  const errorCode = validateBetPositionParams(parameters, { stepBet, auction, isAuctionBetEnabled });

  if (errorCode) {
    yield put(loadBetPositionFail(errorCode));
    yield cancel();
  }

  const response: TGetBetPositionResult = yield call(getBetPosition, {
    httpApi,
    logger,
    parameters,
  });

  if (response.success) {
    const payload = {
      ...response.payload,
      serviceId,
    };

    yield put(loadBetPositionSuccess(payload));
  } else {
    yield put(loadBetPositionFail(response.payload));
  }

  // сбрасывем кэш устаревших данных
  if (!isCacheEnabled) {
    yield put(resetBetPositionCache(serviceId));
  }
}
